"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import type { MouseEvent } from "react";
import { useState } from "react";
import { DropReel } from "@/components/drop-reel";
import { resolveImageUrl } from "@/lib/product-images";
import type { Line } from "@/lib/schemas/lines";
import type { ProductListItem } from "@/lib/schemas/products";

type StyleCarouselProps = {
	line: Line;
	products: ProductListItem[];
	height: number;
	// Initial slide — stagger sibling carousels so they don't all start alike.
	startIndex?: number;
	sizes?: string;
};

// Manually-advanced image carousel for a line: prev/next arrows + dots
// crossfade through its shirt mockups. Falls back to the palette-aware DropReel
// (then a tinted slab) when a line has no rendered mockups yet.
//
// The carousel is often rendered inside a <Link> (the style tiles), so the
// controls stop propagation + prevent default to avoid triggering navigation.
export function StyleCarousel({
	line,
	products,
	height,
	startIndex = 0,
	sizes = "(max-width: 1100px) 50vw, 25vw",
}: StyleCarouselProps) {
	const slides = products
		.map((p) => ({
			slug: p.slug,
			name: p.name,
			url: resolveImageUrl(p.heroImageUrl),
		}))
		.filter((s) => s.url);

	const [index, setIndex] = useState(
		slides.length > 0 ? startIndex % slides.length : 0,
	);

	if (slides.length === 0) {
		if (products[0]) {
			return (
				<DropReel
					line={line}
					product={products[0]}
					height={height}
					showLabel={false}
				/>
			);
		}
		return (
			<div
				className="flex items-center justify-center"
				style={{ height, backgroundColor: line.palette?.paper ?? "#efeeea" }}
			>
				<span className="font-mono text-mono-md uppercase tracking-widest text-muted">
					{line.name}
				</span>
			</div>
		);
	}

	const multiple = slides.length > 1;

	function step(e: MouseEvent, delta: number) {
		e.preventDefault();
		e.stopPropagation();
		setIndex((i) => (i + delta + slides.length) % slides.length);
	}

	function goTo(e: MouseEvent, to: number) {
		e.preventDefault();
		e.stopPropagation();
		setIndex(to);
	}

	return (
		<div
			className="group/carousel relative overflow-hidden bg-[#efeeea]"
			style={{ height }}
		>
			{slides.map((slide, i) => (
				<Image
					key={slide.slug}
					src={slide.url}
					alt={`${slide.name} mockup`}
					fill
					sizes={sizes}
					className={`object-cover object-[50%_30%] transition-opacity duration-500 ease-out ${
						i === index ? "opacity-100" : "opacity-0"
					}`}
				/>
			))}

			{multiple && (
				<>
					<button
						type="button"
						aria-label="Previous shirt"
						onClick={(e) => step(e, -1)}
						className="-translate-y-1/2 absolute top-1/2 left-2 flex h-8 w-8 items-center justify-center rounded-full border border-ink/10 bg-paper/85 text-ink opacity-0 transition-opacity hover:bg-paper focus-visible:opacity-100 group-hover/carousel:opacity-100"
					>
						<ChevronLeft size={16} strokeWidth={1.75} />
					</button>
					<button
						type="button"
						aria-label="Next shirt"
						onClick={(e) => step(e, 1)}
						className="-translate-y-1/2 absolute top-1/2 right-2 flex h-8 w-8 items-center justify-center rounded-full border border-ink/10 bg-paper/85 text-ink opacity-0 transition-opacity hover:bg-paper focus-visible:opacity-100 group-hover/carousel:opacity-100"
					>
						<ChevronRight size={16} strokeWidth={1.75} />
					</button>
					<div className="absolute inset-x-0 bottom-2.5 flex items-center justify-center gap-1.5">
						{slides.map((slide, i) => (
							<button
								key={slide.slug}
								type="button"
								aria-label={`Show shirt ${i + 1}`}
								aria-current={i === index}
								onClick={(e) => goTo(e, i)}
								className={`h-1.5 rounded-full transition-all ${
									i === index ? "w-4 bg-ink" : "w-1.5 bg-ink/30 hover:bg-ink/50"
								}`}
							/>
						))}
					</div>
				</>
			)}
		</div>
	);
}
